(() => {
  'use strict';
  const KEY = 'laflore:first_order_20';
  let editor = Boolean(window.Shopify?.designMode);
  let state = null;
  let settings = null;
  let expirationTimer;
  let countdownTimer;
  let schedulePending = false;
  let cartCompatibility = 'pending';
  let cartRevision = 0;
  const defaultPdpMessage = '{percent}% off your first order · Confirmed at checkout';
  const defaultCartMessage = '{percent}% off your first order on eligible items. Discount confirmed at checkout.';
  function refreshSettings() {
    const node = document.querySelector('[data-first-order-settings]');
    try { settings = node ? JSON.parse(node.textContent) : null; } catch (_) { settings = null; }
    editor ||= settings?.editor === true;
  }
  const { configure, startsAt, expiresAt, contextValid, valid } = window.SpecialPromotion;
  function persist() {
    if (editor) return;
    try {
      if (state) {
        // This page's activation flag is not campaign state. Persisting it makes
        // collection and product tabs continually overwrite each other.
        const { landingCollection, ...saved } = state;
        const serialized = JSON.stringify(saved);
        if (localStorage.getItem(KEY) !== serialized) localStorage.setItem(KEY, serialized);
      }
      else localStorage.removeItem(KEY);
    } catch (_) {}
  }
  function synchronize() {
    if ((settings?.enabled === false || settings?.customerEligible === false)) { if (state) state = { ...state, ...settings }; persist(); return; }
    if (!state || !settings) return;
    state = configure(settings, state);
    state.expiresAt = expiresAt(state, state.startedAt);
    persist();
  }
  function copy(value, fallback = '') {
    return (typeof value === 'string' ? value : fallback).replaceAll('{percent}', String(state.percentage));
  }
  refreshSettings();
  function read() {
    try {
      const value = JSON.parse(localStorage.getItem(KEY));
      if (contextValid(value)) return value;
      localStorage.removeItem(KEY);
    } catch (_) { /* Storage may be disabled. Landing still works in memory. */ }
    return null;
  }
  if (!editor) state = read();
  if (window.SpecialPromotion.context) state = window.SpecialPromotion.context;
  synchronize();
  function activate(landingValue) {
    const config = landingValue ? { textContent: JSON.stringify(landingValue) } : document.querySelector('[data-first-order-config]');
    if (!config && settings?.landingCollection) return activate({ collection: settings.landingCollection });
    if (!config || (settings?.enabled === false || settings?.customerEligible === false)) return;
    try {
      const landing = JSON.parse(config.textContent);
      const value = settings ? configure(settings, state, landing) : landing;
      const previous = contextValid(state) ? state : null;
      const startedAt = previous?.startedAt || Date.now();
      const utm = { ...(previous?.utm || {}) };
      const params = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
        if (params.has(key)) utm[key] = params.get(key).slice(0, 500);
      });
      state = { ...value, startedAt, expiresAt: expiresAt(value, startedAt), utm };
      persist();
    } catch (_) { state = null; }
  }
  function presentationAllowed() {
    return !schedulePending && valid(state) && state.nativePricesVerified === true && cartCompatibility === 'clear';
  }
  async function refreshCartCompatibility(suppliedCart) {
    const revision = ++cartRevision;
    cartCompatibility = 'pending';
    render();
    if (!contextValid(state) || settings?.enabled === false || settings?.customerEligible === false || settings?.nativePricesVerified !== true) {
      cartCompatibility = 'blocked';
      render();
      return;
    }
    try {
      let cart = suppliedCart;
      if (!Array.isArray(cart?.items) || !Number.isFinite(cart.total_discount)) {
        const root = window.theme?.routes?.root_url || '/';
        const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Cart unavailable');
        cart = await response.json();
      }
      if (!Array.isArray(cart?.items) || !Number.isFinite(cart.total_discount)) throw new Error('Unverified cart');
      const conflicting = cart.total_discount > 0 || cart.cart_level_discount_applications?.length > 0 || cart.items.some(item =>
        item.discount_allocations?.length || item.line_level_discount_allocations?.length || item.discounts?.length ||
        item.selling_plan_allocation || item.properties?._promotion || item.properties?._bundle_source || item.properties?._bundle_id);
      let clear = !conflicting;
      if (clear && cart.items.length) {
        if (typeof window.LaFloreCartThreshold?.assess !== 'function') clear = false;
        else {
          const assessment = await window.LaFloreCartThreshold.assess(cart);
          clear = assessment.verified === true && assessment.rate === 0;
        }
      }
      if (revision !== cartRevision) return;
      cartCompatibility = clear ? 'clear' : 'blocked';
    } catch (_) {
      if (revision !== cartRevision) return;
      cartCompatibility = 'blocked';
    }
    render();
  }
  function eligible(node) {
    if ((state.excludedProducts || []).map(String).includes(node.dataset.productId)) return false;
    if ((state.excludedCollections || []).some(handle => (node.dataset.collections || '').split(',').includes(handle))) return false;
    return state.products.length
      ? state.products.map(String).includes(node.dataset.productId)
      : (node.dataset.collections || '').split(',').includes(state.collection);
  }
  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }
  function campaignPrice(price, percentage) {
    return Math.round(price * (100 - percentage) / 100);
  }
  function variantPrice(variant) {
    const price = Number(variant.price);
    const compare = variant.compare_at_price == null || variant.compare_at_price === '' ? 0 : Number(variant.compare_at_price);
    if (!Number.isSafeInteger(price) || price <= 0 || price > Math.floor(Number.MAX_SAFE_INTEGER / 100) || !Number.isSafeInteger(compare) || compare < 0) return null;
    const discounted = campaignPrice(price, state.percentage);
    if (discounted <= 0 || discounted >= price) return null;
    if (compare > price) {
      const special = campaignPrice(compare, state.percentage);
      if (special >= price || state.discountConflict !== 'better') return null;
      // Liquid cannot verify checkout codes, automatic discounts or stacking rules.
      // Keep the existing price until an authoritative checkout integration is available.
      return null;
    }
    return { original: price, discounted };
  }
  function normalPriceFor(node) {
    let normal = node.previousElementSibling;
    while (normal && !normal.classList.contains('price') && !normal.classList.contains('fake-price-wrap')) normal = normal.previousElementSibling;
    return normal;
  }
  function nativePriceMatches(node, result) {
    if (!result || typeof window.theme?.Currency?.formatMoneyText !== 'function') return false;
    if (node.dataset.surface === 'cart') return true;
    let priceHook = node;
    if (['card-badge', 'product-badge', 'form-message'].includes(node.dataset.surface)) {
      const surface = node.dataset.surface === 'card-badge' ? 'collection' : 'product';
      priceHook = [...document.querySelectorAll('[data-campaign-display]')].find(peer =>
        peer.dataset.productId === node.dataset.productId && peer.dataset.sectionId === node.dataset.sectionId && peer.dataset.surface === surface);
    }
    if (!priceHook || priceHook.dataset.priceOwner === 'external') return false;
    const normal = normalPriceFor(priceHook);
    if (!normal) return false;
    const amounts = normal.matches('.igPrice') ? [normal] : [...normal.querySelectorAll('.igPrice')];
    if (!amounts.length) return false;
    // Compare formatting only; never parse display text into a price or overwrite it.
    // Permit native removal of whole-money decimals and currency-symbol placement.
    const key = text => String(text).replace(/[^0-9.,-]/g, '').replace(/[.,]00$/, '').replace(/[.,]/g, '');
    const expected = key(window.theme.Currency.formatMoneyText(result.original));
    return amounts.every(amount => key(amount.textContent) === expected);
  }
  function pricing(node) {
    if (node.dataset.checkoutDiscount === 'true' || node.dataset.priceOwner === 'external') return null;
    const productContext = node.closest('product-info');
    if (productContext?.querySelector('[name="bundles"]:checked, [data-campaign-price-owner="external"]')) return null;
    if (['collection', 'card-badge'].includes(node.dataset.surface) && node.dataset.variants) {
      try {
        const variants = JSON.parse(node.dataset.variants).filter(variant => variant.available);
        if (!variants.length) return null;
        const prices = variants.map(variantPrice);
        // A product-wide badge / From price must be true for every purchasable variant.
        if (prices.some(price => !price)) return null;
        const result = prices.reduce((lowest, price) => price.original < lowest.original ? price : lowest);
        return nativePriceMatches(node, result) ? result : null;
      } catch (_) { return null; }
    }
    if (node.dataset.available === 'false') return null;
    const result = variantPrice({ price: node.dataset.price, compare_at_price: node.dataset.comparePrice });
    return nativePriceMatches(node, result) ? result : null;
  }
  function money(price) {
    return window.theme.Currency.formatMoneyText(price, state.priceFormat === 'currency' ? window.theme.settings?.moneyWithCurrencyFormat : undefined);
  }
  function updateCountdown() {
    const active = presentationAllowed() && state.countdownEnabled && Number.isFinite(state.expiresAt);
    let text = '';
    if (active) {
      const remaining = Math.max(0, Math.ceil((state.expiresAt - Date.now()) / 1000));
      const days = Math.floor(remaining / 86400);
      const time = [Math.floor(remaining / 3600) % 24, Math.floor(remaining / 60) % 60, remaining % 60].map(n => String(n).padStart(2, '0')).join(':');
      text = copy(state.countdownMessage, 'Offer ends in {time}').replaceAll('{time}', `${days ? days + 'd ' : ''}${time}`);
    }
    document.querySelectorAll('[data-campaign-countdown]').forEach(node => {
      const hook = node.closest?.('[data-campaign-display]');
      const permitted = active && (!hook || (state.showProductPrices !== false && eligible(hook) && pricing(hook)));
      node.hidden = !active || !permitted || !text;
      if (active && permitted) setText(node, text);
    });
  }
  function updateCountdownTick() {
    if (!valid(state)) { render(); return; }
    updateCountdown();
    countdownTimer = setTimeout(updateCountdownTick, 1000);
  }
  function render() {
    const active = presentationAllowed();
    document.documentElement?.classList.toggle('special-promotion-visible', active);
    clearTimeout(expirationTimer);
    if (state) {
      const boundary = startsAt(state) > Date.now() ? startsAt(state) : state.expiresAt;
      if (Number.isFinite(boundary) && boundary > Date.now()) expirationTimer = setTimeout(() => { synchronize(); render(); }, Math.min(boundary - Date.now() + 1, 2147483647));
    }
    updateCountdown();
    clearTimeout(countdownTimer);
    if (active && state.countdownEnabled && Number.isFinite(state.expiresAt)) countdownTimer = setTimeout(updateCountdownTick, 1000);
    let cartEligible = false;
    document.querySelectorAll('[data-campaign-display]').forEach(node => {
      const grid = node.dataset.surface === 'collection';
      const cart = node.dataset.surface === 'cart';
      const badge = ['card-badge', 'product-badge'].includes(node.dataset.surface);
      const message = node.dataset.surface === 'form-message';
      const available = grid || cart || node.dataset.surface === 'card-badge' || node.dataset.available !== 'false';
      const priceResult = active && eligible(node) && available ? pricing(node) : null;
      const surfaceEnabled = grid ? state?.showCollectionPrices !== false : state?.showProductPrices !== false;
      const show = Boolean(priceResult) && (badge ? state.badgesEnabled !== false : cart || surfaceEnabled);
      if (cart) { cartEligible ||= show; return; }
      // Hidden configuration nodes may separate the original price and our hook.
      const normal = badge || message ? null : normalPriceFor(node);
      const canPrice = !badge && !message && typeof window.theme?.Currency?.formatMoneyText === 'function';
      node.hidden = !show || (message && !copy(state.pdpMessage, defaultPdpMessage)) || (badge && !copy(state.badge) && !copy(state.badgeSecond, 'First order'));
      if (normal) {
        normal.setAttribute('data-campaign-normal', '');
        normal.hidden = show && canPrice && state.showFakePrice !== false;
      }
      if (!show) return;
      if (message) setText(node.querySelector('[data-campaign-form-message]'), copy(state.pdpMessage, defaultPdpMessage));
      setText(node.querySelector('[data-campaign-badge]'), copy(state.badge));
      setText(node.querySelector('[data-campaign-badge-second]'), copy(state.badgeSecond, 'First order'));
      if (!grid) setText(node.querySelector('[data-campaign-message]'), state.terms);
      const prices = node.querySelector('[data-campaign-prices]');
      if (prices) prices.hidden = !canPrice || state.showFakePrice === false;
      const separator = node.querySelector('[data-campaign-separator]');
      if (separator) separator.hidden = !canPrice || state.showFakePrice === false;
      if (canPrice) {
        const price = priceResult.original;
        const discounted = priceResult.discounted;
        setText(node.querySelector('[data-campaign-from]'), grid && node.dataset.varies === 'true' ? 'From ' : '');
        const original = node.querySelector('[data-campaign-original]');
        if (original) original.hidden = state.showOriginalPrice === false;
        setText(original, money(price));
        setText(node.querySelector('[data-campaign-price]'), money(discounted));
        const savings = node.querySelector('[data-campaign-savings]');
        const amount = money(price - discounted);
        const discount = state.savingFormat === 'money' ? amount : state.savingFormat === 'both' ? `${state.percentage}% (${amount})` : `${state.percentage}%`;
        const text = (state.showSavings === false ? '' : copy(state.priceMessage)).replaceAll('{saving}', amount).replaceAll('{discount}', discount);
        if (savings) { savings.hidden = !text; setText(savings, text); }
      }
    });
    document.querySelectorAll('[data-campaign-cart]').forEach(node => {
      const text = active ? copy(state.cartMessage, defaultCartMessage) : '';
      node.hidden = !active || !cartEligible || !text;
      if (active && cartEligible) setText(node, text);
    });
  }
  // Section settings belong to the collection template. Re-read them on other
  // pages so a merchant's changed deadline also applies to returning visitors.
  async function refreshSectionSchedule() {
    if (!state || !settings || typeof fetch !== 'function' || settings.landingCollection || document.querySelector('[data-first-order-config]') || !['http:', 'https:'].includes(location.protocol)) return;
    const source = state.scheduleSource || { collection: state.collection };
    if (!source.collection) return;
    schedulePending = true;
    render();
    try {
      const root = window.theme?.routes?.root_url || '/';
      const url = new URL(`${root}collections/${encodeURIComponent(source.collection)}`, location.origin);
      if (source.sectionId) url.searchParams.set('section_id', source.sectionId);
      let response = await fetch(url, { credentials: 'same-origin' });
      let node = response.ok ? new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-first-order-config]') : null;
      if (source.sectionId && !node) {
        // The merchant may have removed or replaced the section since activation.
        url.searchParams.delete('section_id');
        response = await fetch(url, { credentials: 'same-origin' });
        node = response.ok ? new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-first-order-config]') : null;
      }
      if (!response.ok) throw new Error('Campaign section unavailable');
      if (node) {
        const landing = JSON.parse(node.textContent);
        if (landing.collection !== source.collection || !Object.prototype.hasOwnProperty.call(landing, 'schedule')) throw new Error('Invalid campaign section');
        state = configure(settings, state, landing);
      } else {
        // Removing the section restores the existing shared Theme settings.
        state = configure(settings, state, { collection: source.collection, sectionId: source.sectionId, schedule: null });
      }
      state.expiresAt = expiresAt(state, state.startedAt);
      persist();
      schedulePending = false;
    } catch (_) { /* Keep normal prices if the current section schedule cannot be verified. */ }
    render();
  }
  // ProductInfo replaces price/CTA fragments then publishes the selected variant.
  // Update persistent image hooks as well; all surfaces use the same calculation.
  function initialize() {
    if (window.theme?.pubsub) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, event => {
        const data = event.data;
        if (!data?.variant) return;
        document.querySelectorAll('[data-campaign-display]').forEach(node => {
          if (node.dataset.productId !== String(data.productId) || ['collection', 'card-badge', 'cart'].includes(node.dataset.surface)) return;
          // A quick view of the same product must not change another section's selection.
          if (data.sectionId && node.dataset.sectionId && node.dataset.sectionId !== String(data.sectionId)) return;
          node.dataset.price = String(data.variant.price);
          node.dataset.comparePrice = String(data.variant.compare_at_price || 0);
          node.dataset.variantId = String(data.variant.id);
          node.dataset.available = String(data.variant.available);
        });
        render();
      });
    }
    activate();
    render();
    refreshSectionSchedule();
    refreshCartCompatibility();
    let scheduled = false;
    const observer = new MutationObserver(records => {
      if (records?.length && records.every(record => (record.target.nodeType === 1 ? record.target : record.target.parentElement)?.closest('[data-campaign-countdown]'))) return;
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; render(); });
    });
    observer.observe(document.body, { childList: true, characterData: true, attributes: true, attributeFilter: ['data-price-owner', 'data-campaign-price-owner'], subtree: true });
    document.addEventListener('shopify:section:load', () => { refreshSettings(); synchronize(); activate(); refreshCartCompatibility(); });
    window.addEventListener('storage', event => { if (!editor && (event.key === KEY || event.key === null)) { state = read(); synchronize(); refreshCartCompatibility(); } });
    window.theme?.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, event => refreshCartCompatibility(event?.cart || event?.detail?.cart));
    ['cart:updated', 'cart:change', 'cart:refresh'].forEach(name => document.addEventListener(name, event => refreshCartCompatibility(event.detail?.cart)));
    document.addEventListener('bundle:change', render);
    document.addEventListener('change', event => { if (event.target.matches?.('[name="bundles"]')) render(); });
    window.addEventListener('pageshow', resume);
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', resume);
    function resume() { if (!editor) { try { localStorage.getItem(KEY); state = read(); } catch (_) {} } synchronize(); refreshCartCompatibility(); }
    setInterval(() => { if (state && !valid(state)) render(); }, 30000);
  }
  initialize();
})();
